import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { X, BarChart3, Loader2, Sparkles, Share2 } from "lucide-react";
import * as htmlToImage from "html-to-image";
import { toast } from "sonner";
import { useLang } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VERDICT_ROW = [
  { key: "MANDA", bg: "#00FFA3", emoji: "🟢" },
  { key: "ESPERA", bg: "#FFD500", emoji: "🟡" },
  { key: "NAO_MANDA", bg: "#FF3366", emoji: "🔴" },
];

export const PatternsModal = ({ token, onClose }) => {
  const { t } = useLang();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [enough, setEnough] = useState(true);
  const [loadingSum, setLoadingSum] = useState(false);
  const shareRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/patterns`, auth)
      .then(({ data }) => setData(data))
      .catch((e) => { console.error("load patterns failed:", e); setData({ total: 0 }); });
  }, []); // eslint-disable-line -- run once on mount; auth token is stable per session

  const total = data?.total || 0;
  const maxV = data ? Math.max(1, ...VERDICT_ROW.map((r) => data.verdicts?.[r.key] || 0)) : 1;

  const fetchSummary = async () => {
    setLoadingSum(true);
    try {
      const { data: res } = await axios.post(`${API}/patterns/summary`, { lang: localStorage.getItem("mnm_lang") || "pt" }, auth);
      setEnough(res.enough);
      setSummary(res.summary);
    } catch (e) { console.error("patterns summary failed:", e); }
    setLoadingSum(false);
  };

  const handleShare = async () => {
    const node = shareRef.current;
    if (!node) return;
    try {
      const dataUrl = await htmlToImage.toPng(node, { pixelRatio: 2, cacheBust: true });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "prapsy-padroes.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "prapsy" });
        return;
      }
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "prapsy-padroes.png";
      a.click();
      toast.success(t.share_saved);
    } catch (e) { console.error("share patterns failed:", e); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" data-testid="patterns-modal">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg bg-[#F4F0E6] border-[4px] border-black rounded-2xl shadow-[8px_8px_0px_0px_#000] my-8"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b-[3px] border-black">
          <h2 className="font-display font-bold text-xl uppercase tracking-tight flex items-center gap-2">
            <BarChart3 size={20} strokeWidth={2.5} /> {t.pat_title}
          </h2>
          <button data-testid="patterns-close-btn" onClick={onClose} className="border-[2px] border-black rounded-full p-1 bg-white hover:bg-[#FFD500] transition-colors">
            <X size={18} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {data === null && (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin" size={26} /></div>
          )}

          {data && total === 0 && (
            <p className="text-center text-[#666] font-semibold py-10" data-testid="patterns-empty">{t.pat_empty}</p>
          )}

          {data && total > 0 && (
            <>
              <div className="border-[3px] border-black rounded-xl bg-[#BD00FF] text-white p-4 text-center shadow-[4px_4px_0px_0px_#000]" data-testid="patterns-total">
                <p className="font-mono2 font-bold text-5xl leading-none">{total}</p>
                <p className="text-xs font-bold uppercase tracking-[0.12em] mt-1">{t.pat_total}</p>
              </div>

              <div className="border-[2px] border-black rounded-xl bg-white p-4">
                <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#444] mb-3">{t.pat_verdicts}</p>
                <div className="space-y-2">
                  {VERDICT_ROW.map((r) => {
                    const c = data.verdicts?.[r.key] || 0;
                    return (
                      <div key={r.key} className="flex items-center gap-2">
                        <span className="text-sm w-24 shrink-0 font-bold">{r.emoji} {t[`verdict_${r.key}`]}</span>
                        <div className="flex-1 h-6 border-[2px] border-black rounded-md bg-[#F4F0E6] overflow-hidden">
                          <div className="h-full" style={{ width: `${(c / maxV) * 100}%`, background: r.bg }} />
                        </div>
                        <span className="font-mono2 font-bold text-sm w-6 text-right">{c}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div className="border-[2px] border-black rounded-xl bg-[#FFD500] p-3 text-center">
                  <p className="font-mono2 font-bold text-2xl leading-none">{data.modes?.send || 0}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wide mt-1">{t.pat_send}</p>
                </div>
                <div className="border-[2px] border-black rounded-xl bg-[#4D9FFF] p-3 text-center">
                  <p className="font-mono2 font-bold text-2xl leading-none">{data.modes?.received || 0}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wide mt-1">{t.pat_received}</p>
                </div>
                <div className="border-[2px] border-black rounded-xl bg-[#FF3366] text-white p-3 text-center">
                  <p className="font-mono2 font-bold text-2xl leading-none">{data.no_filter_count || 0}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wide mt-1">{t.pat_nofilter}</p>
                </div>
              </div>

              {data.top_categories?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#444] mb-2">{t.pat_themes}</p>
                  <div className="flex flex-wrap gap-2">
                    {data.top_categories.map((c) => (
                      <span key={c.label} className="text-xs font-semibold border-[2px] border-black rounded-full px-3 py-1 bg-white">
                        {c.emoji} {c.label} · {c.count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {data.top_read_labels?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#444] mb-2">{t.pat_reads}</p>
                  <div className="flex flex-wrap gap-2">
                    {data.top_read_labels.map((c) => (
                      <span key={c.label} className="text-[11px] font-bold uppercase border-[2px] border-black rounded-full px-3 py-1 bg-[#00FFA3]">
                        {c.label} · {c.count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <button
                data-testid="patterns-share-btn"
                onClick={handleShare}
                className="w-full flex items-center justify-center gap-2 bg-white border-[2px] border-black rounded-xl py-2.5 text-sm font-bold uppercase tracking-wide shadow-[3px_3px_0px_0px_#000] hover:bg-[#FFD500] transition-colors"
              >
                <Share2 size={16} strokeWidth={2.5} /> {t.pat_share}
              </button>

              {summary ? (
                <div className="border-[3px] border-black rounded-xl bg-black text-white p-4 shadow-[4px_4px_0px_0px_#BD00FF]" data-testid="patterns-summary">
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-[#00FFA3] mb-2">{t.pat_summary_title}</p>
                  <p className="text-sm leading-relaxed font-medium">{summary}</p>
                </div>
              ) : (
                <>
                  {!enough && (
                    <p className="text-center text-[#666] text-sm font-semibold" data-testid="patterns-need-more">{t.pat_need_more}</p>
                  )}
                  {enough && (
                    <button
                      data-testid="patterns-summary-btn"
                      onClick={fetchSummary}
                      disabled={loadingSum}
                      className="w-full flex items-center justify-center gap-2 bg-[#00FFA3] border-[3px] border-black rounded-xl py-3 font-display font-bold uppercase tracking-tight text-lg shadow-[4px_4px_0px_0px_#000] transition-[transform,box-shadow] duration-100 hover:shadow-[2px_2px_0px_0px_#000] hover:translate-x-[2px] hover:translate-y-[2px] disabled:opacity-70"
                    >
                      {loadingSum ? <><Loader2 className="animate-spin" size={18} /> {t.pat_summary_loading}</> : <><Sparkles size={18} strokeWidth={2.5} /> {t.pat_summary_btn}</>}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </div>

        {/* hidden shareable card */}
        {data && total > 0 && (
          <div ref={shareRef} style={{ position: "absolute", left: "-9999px", top: 0, width: "440px" }}>
            <div style={{ background: "#F4F0E6", border: "4px solid #000", borderRadius: "24px", padding: "28px", fontFamily: "Outfit, sans-serif" }}>
              <div style={{ fontSize: "28px", fontWeight: 800, marginBottom: "10px" }}>
                <span style={{ color: "#16121f" }}>pra</span><span style={{ color: "#FF3366" }}>psy</span>
              </div>
              <p style={{ fontSize: "13px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", color: "#666", margin: 0 }}>{t.pat_card_title}</p>
              <div style={{ background: "#BD00FF", border: "3px solid #000", borderRadius: "16px", padding: "20px", textAlign: "center", margin: "14px 0" }}>
                <div style={{ fontSize: "56px", fontWeight: 800, color: "#fff", lineHeight: 1 }}>{total}</div>
                <div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", color: "#fff", letterSpacing: "1px", marginTop: "4px" }}>{t.pat_total}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {VERDICT_ROW.map((r) => {
                  const c = data.verdicts?.[r.key] || 0;
                  return (
                    <div key={r.key} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span style={{ width: "96px", fontSize: "13px", fontWeight: 700 }}>{r.emoji} {t[`verdict_${r.key}`]}</span>
                      <div style={{ flex: 1, height: "22px", border: "2px solid #000", borderRadius: "6px", background: "#fff", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${(c / maxV) * 100}%`, background: r.bg }} />
                      </div>
                      <span style={{ width: "24px", textAlign: "right", fontWeight: 800, fontSize: "13px" }}>{c}</span>
                    </div>
                  );
                })}
              </div>
              {data.top_categories?.length > 0 && (
                <p style={{ marginTop: "16px", fontSize: "14px", fontWeight: 700 }}>
                  {t.pat_themes}: {data.top_categories[0].emoji} {data.top_categories[0].label}
                </p>
              )}
              <p style={{ textAlign: "center", fontSize: "12px", fontWeight: 700, color: "#999", marginTop: "18px" }}>{t.pat_card_tagline}</p>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};
